import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import { Notification } from './types';
import { NotificationType } from '@agilelab/plugin-wb-platform-common';
import crossFetch from 'cross-fetch';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  NotificationBinding,
  NotificationBindingDeleteRequest,
  NotificationBindingInsertRequest,
  NotificationBindingPatchRequest,
  NotificationBindingsGetRequest,
  NotificationBindingsGetResponse,
  NotificationKindsGetResponse,
  Webhook,
  WebhookCreateRequest,
  WebhookDeleteRequest,
  WebhooksGetRequest,
  WebhooksGetResponse,
  WebhookPatchRequest,
  WebhookGetRequest,
  WebhookGetResponse,
  HeadersByWebhookGetRequest,
  HeadersByWebhookGetResponse,
  WebhookHeaderUpsertRequest,
  WebhookHeaderUpsertResponse,
  WebhookHeaderDeleteRequest,
  WebhookHeaderDeleteResponse,
} from './WebhookApi';

export declare type NotificationRequestOptions = {
  token?: string;
};

export interface NotificationApi {
  updateNotificationResponse(
    id: string,
    notification_response: any,
    options?: NotificationRequestOptions,
  ): Promise<any>;

  getWebhookHeaders(
    request: HeadersByWebhookGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<HeadersByWebhookGetResponse>;

  upsertWebhookHeader(
    request: WebhookHeaderUpsertRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhookHeaderUpsertResponse>;

  deleteWebhookHeader(
    request: WebhookHeaderDeleteRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhookHeaderDeleteResponse>;

  countUnreadByRecipient(
    recipient: string,
    options?: NotificationRequestOptions,
  ): Promise<{ count: number }>;

  readNotification(
    id: string,
    options?: NotificationRequestOptions,
  ): Promise<any>;

  readAllNotifications(
    recipient: string,
    options?: NotificationRequestOptions,
  ): Promise<any>;

  paginatedByRecipient(
    recipient: string,
    offset: number,
    limit: number,
    kind?: NotificationType,
    isRead?: boolean,
    response_status?: string,
    options?: NotificationRequestOptions,
  ): Promise<{ total: number; notifications: Notification[] }>;

  getNotificationQuestions(
    options?: NotificationRequestOptions,
  ): Promise<{ total: number; notifications: Notification[] }>;

  getNotificationByDataProductInstanceID(
    id_dataproduct_instance: string,
    kind: string,
    options?: NotificationRequestOptions,
  ): Promise<any>;

  addNotificationRequest(
    sender: string,
    recipient: string,
    hook_id: string | null,
    kind: string,
    notification_request: any,
    options?: NotificationRequestOptions,
  ): Promise<any>;

  triggerNotificationEvent(
    entityRef: string,
    eventType: string,
    options?: NotificationRequestOptions,
  ): Promise<any>;

  getWebhook(
    request: WebhookGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhookGetResponse>;

  getWebhooks(
    request: WebhooksGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhooksGetResponse>;

  createWebhook(
    request: WebhookCreateRequest,
    options?: NotificationRequestOptions,
  ): Promise<Webhook>;

  createNotificationBinding(
    request: NotificationBindingInsertRequest,
    options?: NotificationRequestOptions,
  ): Promise<NotificationBinding>;

  updateWebhook(
    request: WebhookPatchRequest,
    options?: NotificationRequestOptions,
  ): Promise<Webhook>;

  deleteWebhooks(
    request: WebhookDeleteRequest,
    options?: NotificationRequestOptions,
  ): Promise<void>;

  getNotificationBindings(
    request: NotificationBindingsGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<NotificationBindingsGetResponse>;

  getNotificationKinds(
    request: {},
    options?: NotificationRequestOptions,
  ): Promise<NotificationKindsGetResponse>;

  patchNotificationBinding(
    request: NotificationBindingPatchRequest,
    options?: NotificationRequestOptions,
  ): Promise<NotificationBinding>;

  deleteNotificationBindings(
    request: NotificationBindingDeleteRequest,
    options?: NotificationRequestOptions,
  ): Promise<void>;
}

export class NotificationClient implements NotificationApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly baseUrlPromise: Promise<string>;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
    this.baseUrlPromise = this.discoveryApi.getBaseUrl('notifications');
  }

  async addNotificationRequest(
    sender: string,
    recipient: string,
    hook_id: string | null,
    kind: string,
    notification_request: any,
    options?: NotificationRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const body = {
      notification_request: notification_request,
      sender: sender,
      recipient: recipient,
      kind: kind.toString(),
      hook_id: hook_id ? hook_id : undefined,
    };

    const response = await this.fetchApi.fetch(`${baseUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
      },
      body: JSON.stringify(body),
    });

    await handleFailedResponse(response);

    return response.json();
  }

  async updateNotificationResponse(
    id: string,
    notification_response: any,
    options?: NotificationRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/${id}/response/?${new URLSearchParams({
        replyToSender: 'true', // TODO: this is to be chosen by the user in the UI
      })}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
        body: JSON.stringify({ notification_response: notification_response }),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async readNotification(
    id: string,
    options?: NotificationRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(`${baseUrl}/${id}/read`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
      },
    });
    await handleFailedResponse(response);

    return response.json();
  }

  async readAllNotifications(
    recipient: string,
    options?: NotificationRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/read/all?${new URLSearchParams({
        recipient,
      })}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async countUnreadByRecipient(
    recipient: string,
    options?: NotificationRequestOptions,
  ): Promise<{ count: number }> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/unread/count?${new URLSearchParams({
        recipient: recipient,
      })}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async getNotificationByDataProductInstanceID(
    id_dataproduct_instance: string,
    kind: string,
    options?: NotificationRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}?${new URLSearchParams({
        kind: kind.toString(),
        id_dataproduct_instance: id_dataproduct_instance,
      })}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    return (await response.json()).notifications;
  }

  /**
   * Returns an object like
   * {
   *     total: total number of questions found
   *     notification: array of timestamps of questions
   * }
   */
  async getNotificationQuestions(
    options?: NotificationRequestOptions,
  ): Promise<{ total: number; notifications: Notification[] }> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}?${new URLSearchParams({
        kind: NotificationType.QUESTION,
        onlyTimestamps: 'true',
      })}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  /**
   * Returns an object like
   * {
   *     total: total number of notifications found
   *     notification: array of notification offset and limited by parameters
   * }
   */
  async paginatedByRecipient(
    recipient: string,
    offset: number,
    limit: number,
    kind?: NotificationType,
    isRead?: boolean,
    response_status?: string,
    options?: NotificationRequestOptions,
  ): Promise<{ total: number; notifications: Notification[] }> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}?${new URLSearchParams({
        recipient: recipient,
        offset: offset.toString(),
        limit: limit.toString(),
        ...(kind ? { kind: kind.toString() } : {}),
        ...(isRead !== undefined ? { isRead: String(isRead) } : {}),
        ...(response_status
          ? { response_status: response_status.toString() }
          : {}),
      })}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async triggerNotificationEvent(
    entityRef: string,
    eventType: string,
    options?: NotificationRequestOptions,
    body: any = {},
  ) {
    const baseUrl = await this.baseUrlPromise;

    this.fetchApi.fetch(
      `${baseUrl}/informative/by-event?${new URLSearchParams({
        entityRef: entityRef,
        event: eventType,
      })}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
        body: JSON.stringify(body),
      },
    );
  }

  async createWebhook(
    request: WebhookCreateRequest,
    options?: NotificationRequestOptions,
  ): Promise<Webhook> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(`${baseUrl}/webhooks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
      body: JSON.stringify(request),
    });
    await handleFailedResponse(response);
    return response.json();
  }

  async updateWebhook(
    request: WebhookPatchRequest,
    options?: NotificationRequestOptions,
  ): Promise<Webhook> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/webhooks/${request.uuid}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(request.body),
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getWebhook(
    request: WebhookGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhookGetResponse> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/webhooks/${request.uuid}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async deleteWebhooks(
    request: WebhookDeleteRequest,
    options?: NotificationRequestOptions,
  ): Promise<void> {
    const baseUrl = await this.baseUrlPromise;
    const url = new URL(`${baseUrl}/webhooks`);

    url.searchParams.append('uuids', request.uuids.join(','));

    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
    });
    await handleFailedResponse(response);
  }

  async deleteNotificationBindings(
    request: NotificationBindingDeleteRequest,
    options?: NotificationRequestOptions,
  ): Promise<void> {
    const baseUrl = await this.baseUrlPromise;
    const url = new URL(`${baseUrl}/webhooks/notification-bindings`);

    url.searchParams.append('ids', request.ids.join(','));
    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
    });
    await handleFailedResponse(response);
  }

  async getWebhooks(
    request: WebhooksGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhooksGetResponse> {
    const baseUrl = await this.baseUrlPromise;

    const url = new URL(`${baseUrl}/webhooks`);

    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== '')
        url.searchParams.append(key, String(value));
    });

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers: {
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
    });

    await handleFailedResponse(response);

    return response.json();
  }

  async getNotificationBindings(
    request: NotificationBindingsGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<NotificationBindingsGetResponse> {
    const baseUrl = await this.baseUrlPromise;

    const url = new URL(`${baseUrl}/webhooks/notification-bindings`);

    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== '')
        url.searchParams.append(key, String(value));
    });

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers: {
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
    });

    await handleFailedResponse(response);

    return response.json();
  }

  async createNotificationBinding(
    request: NotificationBindingInsertRequest,
    options?: NotificationRequestOptions,
  ): Promise<NotificationBinding> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/webhooks/notification-bindings`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(request),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async patchNotificationBinding(
    request: NotificationBindingPatchRequest,
    options?: NotificationRequestOptions,
  ): Promise<NotificationBinding> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/webhooks/notification-bindings/${request.id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(request.body),
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getNotificationKinds(
    _request: {},
    options?: NotificationRequestOptions,
  ): Promise<NotificationKindsGetResponse> {
    const baseUrl = await this.baseUrlPromise;
    const url = new URL(`${baseUrl}/kinds`);

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers: {
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
    });

    await handleFailedResponse(response);

    return response.json();
  }

  async getWebhookHeaders(
    request: HeadersByWebhookGetRequest,
    options?: NotificationRequestOptions,
  ): Promise<HeadersByWebhookGetResponse> {
    const baseUrl = await this.baseUrlPromise;
    const url = new URL(`${baseUrl}/webhooks/${request.webhook_uuid}/headers`);
    if (request.redactWith) {
      url.searchParams.append('redactWith', request.redactWith);
    }
    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers: {
        ...(options?.token && { Authorization: `Bearer ${options.token}` }),
      },
    });
    await handleFailedResponse(response);
    return response.json();
  }

  async upsertWebhookHeader(
    request: WebhookHeaderUpsertRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhookHeaderUpsertResponse> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/webhooks/${request.webhook_uuid}/headers`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
        body: JSON.stringify(request),
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async deleteWebhookHeader(
    request: WebhookHeaderDeleteRequest,
    options?: NotificationRequestOptions,
  ): Promise<WebhookHeaderDeleteResponse> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/webhooks/${request.webhook_uuid}/headers/${encodeURIComponent(
        request.key,
      )}`,
      {
        method: 'DELETE',
        headers: {
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }
}
